/**
 * The billing screen's words: GST, the tax profile, and the states a plan
 * change or an invoice can be in.
 *
 * Kept out of `routes/plan.tsx` for `copy/plan.ts`'s own reason — a judgement
 * about what this screen says is one a test can hold on its own.
 */

import type {
  BillingTaxProfile,
  Entitlement,
  Invoice,
  PendingPlanChange,
  Plan,
  PlanChange,
} from "@magicweave/api";

import { GET_IN_TOUCH, LIMITS_LIVE, money } from "./plan";
import { calendarDay } from "./when";

/**
 * 18%, in one place.
 *
 * `decisions/0025`'s successor for billing: "the Razorpay plan amount and the
 * overage addon are the net price plus 18% (GSTRateBPS)". Basis points for the
 * same reason a multiple is — 1,800 is exact where 0.18 is a float somebody
 * rounded — and importing it everywhere a price is quoted is what keeps this
 * console from ever writing "18%" as a second, driftable copy of the rate the
 * server actually charges at.
 */
export const GST_RATE_BPS = 1800;

/** "18%", built from the one constant above rather than typed out again. */
export const GST_RATE_WORDS = `${GST_RATE_BPS / 100}%`;

/**
 * The two currencies an organisation can be billed in. INR for a profile in
 * India — and for an organisation with no profile yet — and USD anywhere
 * else, as a zero-rated export of services. Never chosen: it follows from
 * the billing profile's country, and the server fixes it once anything has
 * been paid in it.
 */
export type BillingCurrency = "INR" | "USD";

/**
 * The organisation's billing currency, read off the server and never worked
 * out here: `getBilling`'s `currency` first, then the profile's own, then INR
 * — the server's answer for an organisation with no profile.
 */
export function billingCurrency(
  billing?: { currency?: string } | null,
  profile?: { currency?: string } | null,
): BillingCurrency {
  const c = billing?.currency ?? profile?.currency;
  return c === "USD" ? "USD" : "INR";
}

/** Whether a price in this currency carries GST. Only INR does: USD is an export. */
export function chargesGst(currency: string): boolean {
  return currency === "INR";
}

/** What a USD price says where an INR one says GST. */
export const NO_GST_EXPORT = "No GST (export)";

/** "GST included" for INR, "No GST (export)" for USD — beside the one figure paid. */
export function taxNote(currency: string): string {
  return chargesGst(currency) ? GST_INCLUDED : NO_GST_EXPORT;
}

/**
 * Net, tax and gross for one net figure, in minor units.
 *
 * Rounded half up to the paisa, the same rule the server's own GST split
 * documents for splitting a total — there is no lower unit than a paisa to
 * disagree about. This is a **display** computation for a price nobody has
 * been charged yet (a plan in the catalogue, the plan in force, the overage
 * rate): once a real invoice exists, `InvoiceTax` carries the figures Zoho
 * actually computed, and those are rendered as they arrive rather than
 * recomputed here.
 */
export function gstBreakdown(
  net: number,
  currency: string = "INR",
): { net: number; tax: number; gross: number } {
  // A USD price is a zero-rated export: nothing is added to it.
  if (!chargesGst(currency)) return { net, tax: 0, gross: net };
  const tax = Math.round((net * GST_RATE_BPS) / 10_000);
  return { net, tax, gross: net + tax };
}

/**
 * "₹X + 18% GST = ₹Y" — never a price on its own.
 *
 * Every rupee figure this console shows before a charge is taken goes through
 * this, so nobody reading it has to already know GST is coming. `net` is
 * always the server's own figure — a plan's price, the plan's overage rate —
 * and this does arithmetic on it rather than inventing one.
 */
export function gstWords(net: number, currency: string): string {
  const { tax, gross } = gstBreakdown(net, currency);
  if (net === 0 && tax === 0) return money(0, currency);
  if (!chargesGst(currency)) return `${money(net, currency)} · ${NO_GST_EXPORT}`;
  return `${money(net, currency)} + ${GST_RATE_WORDS} GST = ${money(gross, currency)}`;
}

/** What a studio reads beside the one figure it pays. */
export const GST_INCLUDED = "GST included";

/**
 * The price a studio pays, GST included, as one figure — whole rupees, the
 * way a plan is quoted on a card. The split into net and tax is for the
 * review step and the invoice, where `gstBreakdown` and `InvoiceTax` give it
 * to the paisa.
 */
export function grossWords(net: number, currency: string): string {
  const { gross } = gstBreakdown(net, currency);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(gross / 100);
  } catch {
    return `${Math.round(gross / 100)} ${currency}`;
  }
}

/** The gross to the paisa — the figure a review step charges and an overage rate bills at. */
export function grossExactWords(net: number, currency: string): string {
  return money(gstBreakdown(net, currency).gross, currency);
}

/**
 * A plan's net monthly price in the organisation's billing currency, or
 * undefined for a plan with no price there. INR is `price`; USD is
 * `price_usd`, and a plan that costs nothing costs nothing in either.
 */
export function planPrice(
  plan: Pick<Plan, "price" | "currency" | "price_usd">,
  currency: BillingCurrency,
): { net: number; currency: string } | undefined {
  if (currency === "USD") {
    if (plan.price_usd !== null && plan.price_usd !== undefined) {
      return { net: plan.price_usd, currency: "USD" };
    }
    return plan.price <= 0 ? { net: 0, currency: "USD" } : undefined;
  }
  return { net: plan.price, currency: plan.currency };
}

/**
 * "$29 outside India" — said under an INR price before a studio has a billing
 * profile, so somebody billing from elsewhere knows what they would pay.
 * Undefined for a plan with no USD price, or one that costs nothing.
 */
export function outsideIndiaHint(plan: Pick<Plan, "price_usd">): string | undefined {
  const usd = plan.price_usd;
  if (usd === null || usd === undefined || usd <= 0) return undefined;
  return `${grossWords(usd, "USD")} outside India`;
}

/**
 * The suffix on an overage ceiling or an accrued total — the two figures that
 * stay net, by decision, everywhere they are shown.
 *
 * Unlike a plan's price or the overage rate, a ceiling is a number a studio
 * typed and an accrued total is a running sum `setOverage`/`getEntitlement`
 * maintain — grossing either one up here would be a second, driftable
 * computation of what GST turns it into, on the two figures the server never
 * recomputes with tax added. GST is real and is added only at the moment an
 * addon is actually charged — the Razorpay amount, and the invoice line that
 * follows it — which is what this suffix says without doing the arithmetic
 * itself.
 */
export const GST_WHEN_CHARGED = "+ GST when charged";

/** A net overage figure (a ceiling or an accrued total), labelled — never gross. */
export function netOverageWords(minorUnits: number, currency: string): string {
  return `${money(minorUnits, currency)} ${GST_WHEN_CHARGED}`;
}

/**
 * The thirty-six GST state codes, ascending, each with its canonical name.
 *
 * The same table `modules/billing/internal/tax/states.go` holds, minus "97"
 * (Other Territory, not a state a studio is in) — kept here rather than asked
 * for over the wire because a fixed list published by the GST Council is a
 * constant, and there is no route that serves it. Two retired codes are
 * deliberately absent: **25** (Daman & Diu, merged into 26 in 2020) and **28**
 * (Andhra Pradesh before the Telangana split, replaced by 36 and 37).
 */
export const INDIA_STATE_CODES: readonly { code: string; name: string }[] = [
  { code: "01", name: "Jammu & Kashmir" },
  { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" },
  { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" },
  { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "10", name: "Bihar" },
  { code: "11", name: "Sikkim" },
  { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" },
  { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" },
  { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" },
  { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" },
  { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" },
  { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "24", name: "Gujarat" },
  { code: "26", name: "Dadra & Nagar Haveli and Daman & Diu" },
  { code: "27", name: "Maharashtra" },
  { code: "29", name: "Karnataka" },
  { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" },
  { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" },
  { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman & Nicobar Islands" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "38", name: "Ladakh" },
];

const STATE_NAMES = new Map(INDIA_STATE_CODES.map((s) => [s.code, s.name]));

/** A state code's canonical name, or undefined for one not in the table. */
export function stateName(code: string): string | undefined {
  return STATE_NAMES.get(code);
}

const GSTIN_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const GSTIN_FORMAT = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/**
 * The GSTIN check digit, folded base-36 — the same algorithm
 * `modules/billing/internal/tax/gstin.go`'s `CheckDigit` computes: weights
 * alternate 1 and 2 across the first fourteen characters, each product is
 * folded back into base 36 by adding its quotient to its remainder, and the
 * check character is whatever makes the running total a multiple of 36.
 */
function gstinCheckDigit(first14: string): string | undefined {
  let factor = 1;
  let total = 0;
  for (const ch of first14) {
    const value = GSTIN_ALPHABET.indexOf(ch);
    if (value < 0) return undefined;
    const addend = factor * value;
    factor = factor === 1 ? 2 : 1;
    total += Math.floor(addend / 36) + (addend % 36);
  }
  return GSTIN_ALPHABET[(36 - (total % 36)) % 36];
}

/**
 * Validate a GSTIN client-side, in the server's own order, so a mistake is
 * reported as what it actually is rather than as a generic "invalid".
 *
 * **This is a courtesy, not the authority.** The server validates again on
 * `PUT /tax-profile` and its answer is the one that is stored — this exists
 * so a studio hears about a mistyped or transposed character from the form
 * they are typing into rather than from a 422 a beat later. Checksum-shaped:
 * it runs the same folded-base-36 Luhn variant the server does, which is what
 * catches a single mistyped digit or two transposed ones that a format-only
 * regex would wave through.
 */
export function normaliseGSTIN(
  input: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const value = input.trim().toUpperCase();
  if (value.length !== 15) {
    return { ok: false, error: `A GSTIN is 15 characters; this is ${String(value.length)}.` };
  }
  if (!GSTIN_FORMAT.test(value)) {
    return {
      ok: false,
      error:
        "That is not shaped like a GSTIN: two digits of state code, a ten-character PAN, an entity character, a Z and a check character.",
    };
  }
  if (!stateName(value.slice(0, 2))) {
    return { ok: false, error: `${value.slice(0, 2)} is not a GST state code.` };
  }
  const want = gstinCheckDigit(value.slice(0, 14));
  if (want === undefined || value[14] !== want) {
    return {
      ok: false,
      error: "That GSTIN fails its check digit — check for a mistyped character.",
    };
  }
  return { ok: true, value };
}

/** The state a GSTIN is registered in, blank for anything not 15 characters. */
export function gstinStateCode(gstin: string): string {
  return gstin.length === 15 ? gstin.slice(0, 2) : "";
}

export interface TaxProfileFields {
  /** ISO alpha-2; IN unless the studio says otherwise. */
  country: string;
  legalName: string;
  gstin: string;
  stateCode: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  billingEmail: string;
}

export interface TaxProfileErrors {
  legalName?: string;
  gstin?: string;
  stateCode?: string;
  billingEmail?: string;
  addressLine1?: string;
  postalCode?: string;
}

// A shape check, not an authority — the same courtesy-not-authority role
// `normaliseGSTIN` plays. The server's own `format: email` on `TaxProfileSpec`
// is what actually refuses a malformed address.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Every reason this profile cannot be saved yet, keyed by field so the
 * control that is wrong can say so — house rules: "validation belongs in the
 * control and gates the submit".
 *
 * A blank GSTIN is not an error: it is legal, and means an unregistered B2C
 * customer who takes no input tax credit. A state code is required either way
 * — it is the place of supply, and there is no field the server can derive it
 * from when the GSTIN is blank. A blank billing email is legal here too, on
 * the API's own terms — `TaxProfileSpec.billing_email` is optional — but
 * saving one that is not shaped like an email is refused the same way a bad
 * GSTIN is, before the request rather than a beat after it.
 *
 * **A saved profile with no billing email still saves.** Requiring one is
 * `checkoutBlockedByProfile`'s job, not this one's: this screen is also where
 * a studio with no intention of ever paying fills in a GSTIN for its own
 * records, and a save that is refused for a field only a paid plan needs
 * would be the wrong wall for that studio to hit.
 */
export function taxProfileErrors(fields: TaxProfileFields): TaxProfileErrors {
  const errors: TaxProfileErrors = {};
  if (fields.legalName.trim() === "") {
    errors.legalName = "The name on the tax invoice is required.";
  }
  if (fields.country.toUpperCase() !== "IN") {
    // Outside India: an export invoice, with no GSTIN and no state, addressed
    // by post — the server refuses one without an address, a postal code or
    // somewhere to send it.
    if (fields.addressLine1.trim() === "")
      errors.addressLine1 = "The billing address is required.";
    if (fields.postalCode.trim() === "") errors.postalCode = "The postal code is required.";
    if (fields.billingEmail.trim() === "") {
      errors.billingEmail = "Invoices go here, so it is required outside India.";
    } else if (!EMAIL_FORMAT.test(fields.billingEmail.trim())) {
      errors.billingEmail = "That does not look like an email address.";
    }
    return errors;
  }
  if (fields.gstin.trim() !== "") {
    const checked = normaliseGSTIN(fields.gstin);
    if (!checked.ok) errors.gstin = checked.error;
  }
  if (fields.stateCode.trim() === "") {
    errors.stateCode = "Where you are billing from is required — it decides the tax split.";
  } else if (!stateName(fields.stateCode)) {
    errors.stateCode = "Choose a state from the list.";
  }
  if (fields.billingEmail.trim() !== "" && !EMAIL_FORMAT.test(fields.billingEmail.trim())) {
    errors.billingEmail = "That does not look like an email address.";
  }
  return errors;
}

/**
 * Whether a billing profile is saved at all — the checkout gate. Any country
 * passes: India is billed in INR with GST, anywhere else in USD.
 */
export function hasBillingProfile(profile: BillingTaxProfile | null | undefined): boolean {
  return Boolean(profile && profile.legal_name);
}

/**
 * Whether a saved tax profile carries a billing email — required by this
 * console before checkout, though the API itself leaves it optional: it is
 * where a per-charge invoice and a payment-failure notice are both sent, and
 * a studio with neither would pay with no way to hear that a charge failed.
 */
export function hasBillingEmail(profile: BillingTaxProfile | undefined): boolean {
  return Boolean(profile?.billing_email && profile.billing_email.trim() !== "");
}

/**
 * The stable `error.code` the server refuses `changePlan` with when an
 * upgrade would charge a card and there is no tax profile naming India —
 * `api/openapi/control.yaml`'s own words for it. Match on this, never on
 * `error.message`, which house rules reserve for display and may be reworded
 * at any time.
 */
export const TAX_PROFILE_REQUIRED_CODE = "tax_profile_required";

/**
 * Why a paid plan cannot be pressed yet, before the request is even sent — the
 * client-side half of the India-only gate `decisions/0027`'s successor
 * describes, plus this console's own billing-email requirement. `undefined`
 * means the plan is either free or both are already there.
 *
 * The two reasons are told apart because they are different sentences: one is
 * the server's own refusal restated before it happens, and the other is a
 * rule the API itself does not enforce.
 */
export function checkoutBlockedByProfile(
  plan: Plan,
  profile: BillingTaxProfile | undefined,
): { title: string; body: string } | undefined {
  if (plan.price <= 0) return undefined;
  if (!hasBillingProfile(profile)) {
    return {
      title: "Save your billing details before moving to a paid plan",
      body: "Paid plans need your billing details first. Fill in the form above with your billing address and a billing email, then come back.",
    };
  }
  if (!planPrice(plan, billingCurrency(undefined, profile))) {
    return {
      title: `${plan.name} is not on sale outside India yet`,
      body: "It has no US dollar price. Choose another plan, or get in touch.",
    };
  }
  if (!hasBillingEmail(profile)) {
    return {
      title: "Add a billing email before moving to a paid plan",
      body: "Your invoice for every charge, and any payment-failure notice, go there. Add one to the tax profile above, then come back.",
    };
  }
  return undefined;
}

/**
 * A studio is already on its way to Free when `pending` points at it — a
 * scheduled cancellation `control.yaml`'s own note on `changePlan` says is
 * never undone: re-picking any paid plan afterwards, the plan it is on
 * included, is a fresh checkout charged in full today, with nothing credited
 * for the days left on the old cycle.
 *
 * Free is read off `plans` by price, the same structural test
 * `checkoutBlockedByProfile` above uses, never by comparing `pending`'s code
 * or name to a literal — `pending.code` is compared to `candidate.code`
 * here, one value against another, which names nobody.
 */
export function scheduledCancelWarning(
  pending: PendingPlanChange | null | undefined,
  plans: Plan[],
  plan: Plan,
): { title: string; body: string } | undefined {
  if (!pending || plan.price <= 0) return undefined;
  const pendingPlan = plans.find((candidate) => candidate.code === pending.code);
  if (!pendingPlan || pendingPlan.price > 0) return undefined;
  return {
    title: "Your plan is already ending",
    body: "Your current plan is already set to end. Choosing a paid plan now starts a new subscription at full price today.",
  };
}

/** An invoice's status, in the words a studio reads it in. */
export function invoiceStatusWords(status: Invoice["status"]): {
  label: string;
  tone: "neutral" | "warning" | "positive" | "critical";
} {
  switch (status) {
    case "paid":
      return { label: "Paid", tone: "positive" };
    case "open":
      return { label: "Open", tone: "warning" };
    case "draft":
      return { label: "Draft", tone: "neutral" };
    case "void":
      return { label: "Void", tone: "critical" };
  }
}

/** An applied move that starts on a later day, rather than now. */
export function isScheduled(result: PlanChange): boolean {
  return result.state === "applied" && !result.immediate && Boolean(result.effective_on);
}

/**
 * The sentence a finished move is said in, read off the result's `state` —
 * a 200 is not a moved plan. Checkout's last step.
 */
export function moveDoneWords(
  result: PlanChange,
  plan: Plan,
  current: Entitlement,
  failure?: string,
): { tone: "positive" | "critical" | "neutral"; title: string; detail: string } {
  switch (result.state) {
    case "applied":
      return !isScheduled(result)
        ? {
            tone: "positive",
            title: `You're on ${plan.name}`,
            detail: LIMITS_LIVE,
          }
        : {
            tone: "positive",
            title: `${plan.name} is scheduled`,
            detail: `It starts on ${calendarDay(result.effective_on ?? "")}. Until then nothing changes.`,
          };
    case "charged":
      return {
        tone: "positive",
        title: `${plan.name} is paid for`,
        detail: "Your allowance updates in a moment. Do not pay again.",
      };
    case "failed":
      return {
        tone: "critical",
        title: "The payment was refused",
        detail: `${failure ?? "Nothing was charged."} You are still on ${current.plan?.name ?? "your current plan"}.`,
      };
    case "held":
      return {
        tone: "critical",
        title: "This change needs a person",
        detail: `${GET_IN_TOUCH} Don't try again.`,
      };
    case "requested":
      return {
        tone: "neutral",
        title: `${plan.name} is waiting on payment`,
        detail: "Nothing has been charged. Choose the plan again to retry.",
      };
  }
}

/** Where a paid-for plan change has got to, read while checkout waits on it. */
export type Settlement = "waiting" | "charged" | "applied" | "failed" | "held";

/** The sentence checkout's last step says while a payment settles, and once it has. */
export function settlingWords(
  settled: Settlement,
  plan: Plan,
  current: Entitlement,
  failure?: string,
): { tone: "positive" | "critical" | "neutral"; title: string; detail: string } {
  if (settled === "waiting") {
    return {
      tone: "neutral",
      title: "Confirming your payment…",
      detail: `Razorpay has your payment. You move to ${plan.name} as soon as it confirms — do not pay again.`,
    };
  }
  return moveDoneWords(
    { from: current.plan?.code ?? "", to: plan.code, state: settled, immediate: true },
    plan,
    current,
    failure,
  );
}
